import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Mail, 
  Eye, 
  Send, 
  Settings, 
  TrendingUp,
  BarChart3,
  Package,
  ShoppingCart,
  Bell,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import {
  getAllTemplates,
  getTemplateAnalytics,
  toggleTemplateEnabled,
  EmailTemplateConfig,
  TemplateAnalytics,
} from '@/services/emailTemplateConfigService';
import { TemplatePreviewModal } from '@/components/admin/email/TemplatePreviewModal';
import { TemplateConfigEditor } from '@/components/admin/email/TemplateConfigEditor';
import { SmartTestDialog } from '@/components/admin/email/SmartTestDialog';
import { TemplateAnalyticsPanel } from '@/components/admin/email/TemplateAnalyticsPanel';

const categoryIcons = {
  transactional: ShoppingCart,
  marketing: Package,
  notification: Bell,
};

const categoryColors = {
  transactional: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  marketing: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  notification: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
};

const AdminEmailTemplates = () => {
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateConfig | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isConfigEditorOpen, setIsConfigEditorOpen] = useState(false);
  const [isTestEmailOpen, setIsTestEmailOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);

  // Fetch templates
  const { data: templates, isLoading } = useQuery({
    queryKey: ['email-template-configs'],
    queryFn: getAllTemplates,
  });

  // Fetch analytics
  const { data: analytics } = useQuery({
    queryKey: ['email-template-analytics'],
    queryFn: () => getTemplateAnalytics(),
  });

  // Toggle template enabled status
  const toggleEnabledMutation = useMutation({
    mutationFn: ({ templateKey, enabled }: { templateKey: string; enabled: boolean }) =>
      toggleTemplateEnabled(templateKey, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-template-configs'] });
      toast.success('Template status updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update template: ${error.message}`);
    },
  });

  // Get analytics for a specific template
  const getTemplateStats = (templateKey: string): TemplateAnalytics | undefined => {
    return analytics?.find((a) => a.template_key === templateKey);
  };

  // Filter templates by category
  const filteredTemplates = templates?.filter((template) => {
    if (selectedCategory === 'all') return true;
    return template.category === selectedCategory;
  });

  // Group templates by category
  const templatesByCategory = templates?.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {} as Record<string, EmailTemplateConfig[]>);

  const handlePreview = (template: EmailTemplateConfig) => {
    setSelectedTemplate(template);
    setIsPreviewOpen(true);
  };

  const handleTestEmail = (template: EmailTemplateConfig) => {
    setSelectedTemplate(template);
    setIsTestEmailOpen(true);
  };

  const handleEditConfig = (template: EmailTemplateConfig) => {
    setSelectedTemplate(template);
    setIsConfigEditorOpen(true);
  };

  const handleViewAnalytics = (template: EmailTemplateConfig) => {
    setSelectedTemplate(template);
    setIsAnalyticsOpen(true);
  };

  const handleConfigSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['email-template-configs'] });
    queryClient.invalidateQueries({ queryKey: ['email-template-analytics'] });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Email Templates</h1>
        <p className="text-muted-foreground">
          Manage React-powered email templates with real-time analytics and configuration
        </p>
      </div>

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-6">
        <TabsList>
          <TabsTrigger value="all">All Templates</TabsTrigger>
          <TabsTrigger value="transactional">
            <ShoppingCart className="h-4 w-4 mr-2" />
            Transactional
          </TabsTrigger>
          <TabsTrigger value="marketing">
            <Package className="h-4 w-4 mr-2" />
            Marketing
          </TabsTrigger>
          <TabsTrigger value="notification">
            <Bell className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Templates Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {filteredTemplates?.map((template) => {
          const stats = getTemplateStats(template.template_key);
          const CategoryIcon = categoryIcons[template.category as keyof typeof categoryIcons];
          const categoryColorClass = categoryColors[template.category as keyof typeof categoryColors];

          return (
            <Card key={template.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${categoryColorClass}`}>
                      <CategoryIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{template.template_name}</CardTitle>
                      <CardDescription className="text-sm mt-1">
                        {template.description}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={template.enabled ? 'default' : 'secondary'}
                      className="flex items-center gap-1"
                    >
                      {template.enabled ? (
                        <CheckCircle2 className="h-3 w-3" />
                      ) : (
                        <XCircle className="h-3 w-3" />
                      )}
                      {template.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                </div>

                {/* Category Badge */}
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className={categoryColorClass}>
                    {template.category}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {template.template_key}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Analytics Stats */}
                {stats && stats.total_sent > 0 && (
                  <div 
                    className="grid grid-cols-4 gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors"
                    onClick={() => handleViewAnalytics(template)}
                  >
                    <div className="text-center">
                      <div className="text-2xl font-bold">{stats.total_sent}</div>
                      <div className="text-xs text-muted-foreground">Sent</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {stats.delivery_rate.toFixed(0)}%
                      </div>
                      <div className="text-xs text-muted-foreground">Delivered</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {stats.open_rate.toFixed(0)}%
                      </div>
                      <div className="text-xs text-muted-foreground">Opened</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {stats.click_rate.toFixed(0)}%
                      </div>
                      <div className="text-xs text-muted-foreground">Clicked</div>
                    </div>
                  </div>
                )}
                
                {/* View Analytics Button for templates without stats */}
                {(!stats || stats.total_sent === 0) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewAnalytics(template)}
                    className="w-full"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    View Analytics
                  </Button>
                )}

                {/* Configuration Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">From:</span>
                    <span className="font-medium">
                      {template.from_name} &lt;{template.from_email}&gt;
                    </span>
                  </div>
                  {template.reply_to && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Reply To:</span>
                      <span className="font-medium">{template.reply_to}</span>
                    </div>
                  )}
                </div>

                {/* Variables */}
                <div>
                  <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    Available Variables:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {(template.variables as string[]).slice(0, 4).map((variable) => (
                      <Badge key={variable} variant="outline" className="text-xs">
                        {variable}
                      </Badge>
                    ))}
                    {(template.variables as string[]).length > 4 && (
                      <Badge variant="outline" className="text-xs">
                        +{(template.variables as string[]).length - 4} more
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreview(template)}
                    className="flex-1"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestEmail(template)}
                    className="flex-1"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Test
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditConfig(template)}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>

                  <Switch
                    checked={template.enabled}
                    onCheckedChange={(checked) =>
                      toggleEnabledMutation.mutate({
                        templateKey: template.template_key,
                        enabled: checked,
                      })
                    }
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredTemplates?.length === 0 && (
        <Card className="py-12">
          <CardContent className="text-center">
            <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No templates found</h3>
            <p className="text-muted-foreground">
              {selectedCategory === 'all'
                ? 'No email templates are configured yet.'
                : `No ${selectedCategory} templates found.`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Overall Analytics Summary */}
      {analytics && analytics.length > 0 && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Overall Email Performance
            </CardTitle>
            <CardDescription>
              Aggregate statistics across all email templates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <TrendingUp className="h-8 w-8 mx-auto mb-2 text-primary" />
                <div className="text-3xl font-bold">
                  {analytics.reduce((sum, a) => sum + a.total_sent, 0)}
                </div>
                <div className="text-sm text-muted-foreground">Total Sent</div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">
                  {analytics.reduce((sum, a) => sum + a.total_delivered, 0)}
                </div>
                <div className="text-sm text-muted-foreground">Delivered</div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">
                  {analytics.reduce((sum, a) => sum + a.total_opened, 0)}
                </div>
                <div className="text-sm text-muted-foreground">Opened</div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-purple-600">
                  {analytics.reduce((sum, a) => sum + a.total_clicked, 0)}
                </div>
                <div className="text-sm text-muted-foreground">Clicked</div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-orange-600">
                  {templates?.filter((t) => t.enabled).length || 0}/{templates?.length || 0}
                </div>
                <div className="text-sm text-muted-foreground">Active</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Modal */}
      <TemplatePreviewModal
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        template={selectedTemplate}
      />

      {/* Config Editor Modal */}
      <TemplateConfigEditor
        open={isConfigEditorOpen}
        onOpenChange={setIsConfigEditorOpen}
        template={selectedTemplate}
        onSaved={handleConfigSaved}
      />

      {/* Smart Test Dialog */}
      <SmartTestDialog
        open={isTestEmailOpen}
        onOpenChange={setIsTestEmailOpen}
        template={selectedTemplate}
      />

      {/* Analytics Panel Modal */}
      <TemplateAnalyticsPanel
        open={isAnalyticsOpen}
        onOpenChange={setIsAnalyticsOpen}
        template={selectedTemplate}
        analytics={selectedTemplate ? getTemplateStats(selectedTemplate.template_key) : undefined}
        allAnalytics={analytics || []}
      />
    </div>
  );
};

export default AdminEmailTemplates;
